import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Gavel as GavelIcon,
  Security as SecurityIcon,
  Business as BusinessIcon,
  Copyright as CopyrightIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';

interface LegalContent {
  title: string;
  subtitle: string;
  companyInfo: {
    title: string;
    description: string;
    businessType: string;
    businessDescription: string;
    serviceType: string;
    serviceDescription: string;
  };
  regulatoryCompliance: {
    title: string;
    description: string;
    items: {
      title: string;
      description: string;
    }[];
  };
  intellectualProperty: {
    title: string;
    description: string;
    items: {
      title: string;
      description: string;
    }[];
  };
  disclaimers: {
    title: string;
    description: string;
    items: {
      title: string;
      description: string;
    }[];
  };
  contactEmail: string;
}

// Default content (fallback)
const defaultContent: LegalContent = {
  title: 'Legal Information',
  subtitle: 'Important legal information about Goat Cards and our services',
  companyInfo: {
    title: 'Company Information',
    description: 'Goat Cards is a sports card marketplace and information platform dedicated to connecting collectors with premium sports cards and memorabilia.',
    businessType: 'Business Type: Sports Card Marketplace',
    businessDescription: 'Online platform for sports card trading and information',
    serviceType: 'Service: Card Information & Trading',
    serviceDescription: 'Real-time market data, card listings, and trading facilitation'
  },
  regulatoryCompliance: {
    title: 'Regulatory Compliance',
    description: 'Goat Cards operates in compliance with applicable laws and regulations governing online marketplaces and sports memorabilia trading.',
    items: [
      {
        title: 'Consumer Protection Laws',
        description: 'Compliance with consumer rights and protection regulations'
      },
      {
        title: 'Data Protection Regulations',
        description: 'GDPR, CCPA, and other privacy law compliance'
      },
      {
        title: 'Financial Transaction Laws',
        description: 'Secure payment processing and financial regulations'
      }
    ]
  },
  intellectualProperty: {
    title: 'Intellectual Property',
    description: 'All content, trademarks, and intellectual property on the Goat Cards platform are protected by applicable copyright and trademark laws.',
    items: [
      {
        title: 'Platform Content',
        description: 'All website content, design, and functionality are owned by Goat Cards'
      },
      {
        title: 'Trademark Protection',
        description: "'Goat Cards' and related marks are protected trademarks"
      },
      {
        title: 'Third-Party Rights',
        description: 'Sports team logos and player images are property of their respective owners'
      }
    ]
  },
  disclaimers: {
    title: 'Important Disclaimers',
    description: 'Please read these important disclaimers regarding our services and platform usage.',
    items: [
      {
        title: 'Investment Risk',
        description: 'Sports card values can fluctuate. Past performance does not guarantee future results.'
      },
      {
        title: 'Market Data',
        description: 'Market data is provided for informational purposes only and may not be real-time.'
      },
      {
        title: 'Third-Party Services',
        description: 'We may link to third-party services. We are not responsible for their content or practices.'
      }
    ]
  },
  contactEmail: 'legal@goatcards.shop'
};

const LegalPage: React.FC = () => {
  const { mode } = useCustomTheme();
  const [content, setContent] = useState<LegalContent>(defaultContent);
  
  // Determine colors based on theme mode
  const isLightMode = mode === 'light';
  const textColor = isLightMode ? '#000000' : '#FFFFFF';
  const secondaryTextColor = isLightMode ? '#4A5568' : '#CCCCCC';

  // Load content from localStorage on component mount
  useEffect(() => {
    const savedContent = localStorage.getItem('legalContent');
    if (savedContent) {
      try {
        const parsedContent = JSON.parse(savedContent);
        setContent(parsedContent);
      } catch (error) {
        console.error('Error loading saved content:', error);
        setContent(defaultContent);
      }
    }
  }, []);

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 800,
            mb: 3,
            color: textColor,
            fontSize: { xs: '2.5rem', md: '3.5rem' },
            letterSpacing: '-0.02em',
          }}
        >
          {content.title}
        </Typography>
        <Typography
          variant="h5"
          sx={{
            color: secondaryTextColor,
            maxWidth: 800,
            mx: 'auto',
            lineHeight: 1.6,
          }}
        >
          {content.subtitle}
        </Typography>
      </Box>

      {/* Company Information */}
      <Paper
        elevation={2}
        sx={{
          p: 4,
          mb: 4,
          background: isLightMode ? '#f8f9fa' : '#1a1a1a',
          borderRadius: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <BusinessIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: textColor,
            }}
          >
            {content.companyInfo.title}
          </Typography>
        </Box>
        <Typography
          variant="body1"
          sx={{
            fontSize: '1.1rem',
            lineHeight: 1.8,
            color: secondaryTextColor,
            mb: 3,
          }}
        >
          {content.companyInfo.description}
        </Typography>
        <List>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon sx={{ color: isLightMode ? '#000000' : '#FFFFFF' }} />
            </ListItemIcon>
            <ListItemText 
              primary={content.companyInfo.businessType}
              secondary={content.companyInfo.businessDescription}
              primaryTypographyProps={{ color: textColor, fontWeight: 600 }}
              secondaryTypographyProps={{ color: secondaryTextColor }}
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon sx={{ color: isLightMode ? '#000000' : '#FFFFFF' }} />
            </ListItemIcon>
            <ListItemText 
              primary={content.companyInfo.serviceType}
              secondary={content.companyInfo.serviceDescription}
              primaryTypographyProps={{ color: textColor, fontWeight: 600 }}
              secondaryTypographyProps={{ color: secondaryTextColor }}
            />
          </ListItem>
        </List>
      </Paper>

      {/* Regulatory Compliance */}
      <Paper
        elevation={2}
        sx={{
          p: 4,
          mb: 4,
          background: isLightMode ? '#f8f9fa' : '#1a1a1a',
          borderRadius: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <GavelIcon sx={{ fontSize: 40, color: isLightMode ? '#000000' : '#FFFFFF', mr: 2 }} />
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: textColor,
            }}
          >
            {content.regulatoryCompliance.title}
          </Typography>
        </Box>
        <Typography
          variant="body1"
          sx={{
            fontSize: '1.1rem',
            lineHeight: 1.8,
            color: secondaryTextColor,
            mb: 3,
          }}
        >
          {content.regulatoryCompliance.description}
        </Typography>
        <List>
          {content.regulatoryCompliance.items.map((item, index) => (
            <ListItem key={index}>
              <ListItemIcon>
                <SecurityIcon sx={{ color: isLightMode ? '#000000' : '#FFFFFF' }} />
              </ListItemIcon>
              <ListItemText 
                primary={item.title}
                secondary={item.description}
                primaryTypographyProps={{ color: textColor, fontWeight: 600 }}
                secondaryTypographyProps={{ color: secondaryTextColor }}
              />
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Intellectual Property */}
      <Paper
        elevation={2}
        sx={{
          p: 4,
          mb: 4,
          background: isLightMode ? '#f8f9fa' : '#1a1a1a',
          borderRadius: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <CopyrightIcon sx={{ fontSize: 40, color: isLightMode ? '#000000' : '#FFFFFF', mr: 2 }} />
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: textColor,
            }}
          >
            {content.intellectualProperty.title}
          </Typography>
        </Box>
        <Typography
          variant="body1"
          sx={{
            fontSize: '1.1rem',
            lineHeight: 1.8,
            color: secondaryTextColor,
            mb: 3,
          }}
        >
          {content.intellectualProperty.description}
        </Typography>
        <List>
          {content.intellectualProperty.items.map((item, index) => (
            <ListItem key={index}>
              <ListItemIcon>
                <WarningIcon sx={{ color: isLightMode ? '#000000' : '#FFFFFF' }} />
              </ListItemIcon>
              <ListItemText 
                primary={item.title}
                secondary={item.description}
                primaryTypographyProps={{ color: textColor, fontWeight: 600 }}
                secondaryTypographyProps={{ color: secondaryTextColor }}
              />
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Disclaimers */}
      <Paper
        elevation={2}
        sx={{
          p: 4,
          mb: 4,
          background: isLightMode ? '#f8f9fa' : '#1a1a1a',
          borderRadius: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <WarningIcon sx={{ fontSize: 40, color: isLightMode ? '#000000' : '#FFFFFF', mr: 2 }} />
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: textColor,
            }}
          >
            {content.disclaimers.title}
          </Typography>
        </Box>
        <Typography
          variant="body1"
          sx={{
            fontSize: '1.1rem',
            lineHeight: 1.8,
            color: secondaryTextColor,
            mb: 3,
          }}
        >
          {content.disclaimers.description}
        </Typography>
        <List>
          {content.disclaimers.items.map((item, index) => (
            <ListItem key={index}>
              <ListItemIcon>
                <WarningIcon sx={{ color: isLightMode ? '#000000' : '#FFFFFF' }} />
              </ListItemIcon>
              <ListItemText 
                primary={item.title}
                secondary={item.description}
                primaryTypographyProps={{ color: textColor, fontWeight: 600 }}
                secondaryTypographyProps={{ color: secondaryTextColor }}
              />
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Contact Information */}
      <Box sx={{ textAlign: 'center', mt: 6 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            mb: 3,
            color: textColor,
          }}
        >
          Legal Inquiries
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: '1.1rem',
            color: secondaryTextColor,
            mb: 2,
          }}
        >
          For legal inquiries or questions about this information, please contact:
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: secondaryTextColor,
            fontWeight: 600,
          }}
        >
          {content.contactEmail}
        </Typography>
      </Box>
    </Container>
  );
};

export default LegalPage; 